import {
  Block,
  CheckCircle,
  FiberNew,
  Publish,
  Store,
  Unpublished,
} from "@mui/icons-material";
import {
  Box,
  Fade,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import styled, { keyframes } from "styled-components";
import { useToastStore, useUserStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { UserRole } from "../../../utils";
import { getFirstDayOfMonth } from "../../../utils/methods/inititalDateTimeFilter";
import { fetchConcessionariaAnalitico } from "../repository/AdminRepository";
import { ConcessionariaAnaliticoData, RelatorioFilterValues } from "../types/types";
import { RelatorioFilterBar } from "./RelatorioFilterBar";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Filler, Tooltip, Legend);

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AnimatedBox = styled.div<{ $delay?: number }>`
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  opacity: 0;
`;

const CHART_PALETTE = [
  colorPrimary, "#10b981", "#0284c7", "#8b5cf6", "#ef4444",
  "#f59e0b", "#ec4899", "#14b8a6", "#6366f1", "#78716c",
  "#d946ef", "#06b6d4", "#84cc16", "#f97316", "#a855f7",
  "#22d3ee", "#fb923c", "#a3e635", "#e879f9", "#34d399",
];

function formatCurrency(val: number): string {
  return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });
}

function formatNumber(val: number | null): string {
  if (val == null) return "0";
  return val.toLocaleString("pt-BR");
}

function formatMinutesToReadable(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return `${h}h ${m > 0 ? m + "min" : ""}`.trim();
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return `${d}d ${rh > 0 ? rh + "h" : ""}`.trim();
}

interface KpiCardProps {
  label: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  delay: number;
}

function KpiCard({ label, value, subtitle, icon, accentColor, bgColor, delay }: KpiCardProps) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          border: "1px solid #f1f5f9",
          height: "100%",
          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            borderColor: accentColor + "40",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography
            fontSize="0.7rem"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={1.5}
            color="#94a3b8"
          >
            {label}
          </Typography>
          <Box
            sx={{
              bgcolor: bgColor,
              color: accentColor,
              p: 1,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography
            fontSize="2rem"
            fontWeight={900}
            letterSpacing={-1.5}
            color={accentColor}
            lineHeight={1}
          >
            {value}
          </Typography>
          <Typography fontSize="0.7rem" fontWeight={600} color={accentColor + "99"}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 3,
            bgcolor: accentColor + "30",
          }}
        />
      </Paper>
    </AnimatedBox>
  );
}

function KpiCardSkeleton({ delay }: { delay: number }) {
  return (
    <AnimatedBox $delay={delay}>
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", overflow: "hidden", position: "relative" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={100} height={16} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Skeleton variant="text" width={80} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={14} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: 3, bgcolor: "#e2e8f0" }} />
      </Paper>
    </AnimatedBox>
  );
}

function ChartSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={200} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: 2 }} />
    </Paper>
  );
}

function TableSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
      <Skeleton variant="text" width={180} height={24} sx={{ mb: 2, borderRadius: 1 }} />
      {Array.from({ length: 5 }).map((_, i) => (
        <Box key={i} display="flex" alignItems="center" gap={2} py={1.5}>
          <Skeleton variant="text" width="60%" height={18} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={60} height={18} sx={{ borderRadius: 1, ml: "auto" }} />
        </Box>
      ))}
    </Paper>
  );
}

const thSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: 1.5,
  color: "#94a3b8",
  borderBottom: "2px solid #f1f5f9",
  py: 1.5,
  whiteSpace: "nowrap" as const,
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        font: { size: 11, weight: 600 as const },
        color: "#475569",
      },
    },
    tooltip: {
      backgroundColor: "#1e293b",
      titleFont: { size: 13, weight: 700 as const },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: (ctx: any) => {
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0";
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        },
      },
    },
  },
};

export function ConcessionariaAnaliticoTab() {
  const [filters, setFilters] = useState<RelatorioFilterValues>({
    grupo: "todos",
    concessionaria: "todos",
    dataInicio: getFirstDayOfMonth(),
    dataFim: new Date().toISOString().split("T")[0] || "",
  });
  const [dashboard, setDashboard] = useState<ConcessionariaAnaliticoData | null>(null);
  const [loading, setLoading] = useState(true);
  const { setToast } = useToastStore();
  const jaCarregouRef = useRef(false);
  const { user } = useUserStore();

  const fetchDash = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchConcessionariaAnalitico({
        id_grupo_concessionaria: filters.grupo !== "todos" ? Number(filters.grupo) : null,
        id_concessionaria: filters.concessionaria !== "todos" ? Number(filters.concessionaria) : null,
        data_inicio: filters.dataInicio || null,
        data_fim: filters.dataFim || null,
      });
      if (!result?.success) {
        setToast({ variant: "error", message: result.message });
        return;
      }
      setDashboard(result.data);
    } catch {
      setToast({ variant: "error", message: "Erro. Contate o administrador." });
    } finally {
      setLoading(false);
    }
  }, [filters, setToast]);

  useEffect(() => {
    if (jaCarregouRef.current) return;
    jaCarregouRef.current = true;
    fetchDash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const volumeVendas = dashboard?.volume_venda_concessionaria ?? [];
  const tempoMedio = dashboard?.tempo_medio_venda_concessionaria ?? [];
  const mediaAnuncios = dashboard?.media_anuncios_mensais_concessionaria ?? [];
  const receita = dashboard?.receita_gerada_por_concessionaria ?? [];
  const porCidade = dashboard?.concessionarias_por_cidade ?? [];
  const porUf = dashboard?.concessionarias_por_uf ?? [];
  const porGrupo = dashboard?.concessionarias_por_grupo ?? [];
  const desempenho = dashboard?.desempenho_concessionaria ?? [];

  const maxVendas = Math.max(...tempoMedio.map((t) => t.total_vendas), 1);

  // --- Volume de Vendas chart ---
  const volumeVendasChartData = {
    labels: volumeVendas.map((v) => v.concessionaria_label),
    datasets: [
      {
        label: "Valor Vendido (R$)",
        data: volumeVendas.map((v) => v.valor),
        backgroundColor: colorPrimary + "cc",
        hoverBackgroundColor: colorPrimary,
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y",
      },
      {
        label: "Qtd Anúncios Vendidos",
        data: volumeVendas.map((v) => v.qtd_anuncios_vendidos),
        backgroundColor: "#0284c7cc",
        hoverBackgroundColor: "#0284c7",
        borderRadius: 6,
        maxBarThickness: 32,
        yAxisID: "y1",
      },
    ],
  };

  const volumeVendasChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: 600 as const } },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            if (ctx.datasetIndex === 0) return ` ${ctx.dataset.label}: ${formatCurrency(ctx.parsed.x)}`;
            return ` ${ctx.dataset.label}: ${ctx.parsed.x}`;
          },
        },
      },
    },
    scales: {
      y: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
      x: {
        position: "top" as const,
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 10 },
          color: "#94a3b8",
          callback: (val: any) => formatCurrency(Number(val)),
        },
      },
      y1: { display: false },
    },
  };

  // --- Receita chart ---
  const receitaChartData = {
    labels: receita.map((r) => r.concessionaria_label),
    datasets: [
      {
        label: "Receita (R$)",
        data: receita.map((r) => r.receita),
        backgroundColor: CHART_PALETTE.slice(0, receita.length),
        borderRadius: 6,
        maxBarThickness: 32,
      },
    ],
  };

  const receitaChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => ` Receita: ${formatCurrency(ctx.parsed.x)}`,
        },
      },
    },
    scales: {
      y: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
      x: {
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 10 },
          color: "#94a3b8",
          callback: (val: any) => formatCurrency(Number(val)),
        },
      },
    },
  };

  // --- Media Anuncios chart ---
  const mediaAnunciosChartData = {
    labels: mediaAnuncios.map((m) => m.concessionaria_label),
    datasets: [
      {
        label: "Média Mensal",
        data: mediaAnuncios.map((m) => m.media_anuncios_mensais),
        backgroundColor: "#8b5cf6cc",
        hoverBackgroundColor: "#8b5cf6",
        borderRadius: 6,
        maxBarThickness: 32,
      },
    ],
  };

  const mediaAnunciosChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
      x: {
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 11 }, color: "#94a3b8", precision: 0 },
      },
    },
  };

  // --- Doughnut data ---
  const cidadeChartData = {
    labels: porCidade.map((c) => c.cidade_label),
    datasets: [{
      data: porCidade.map((c) => c.qtd_per_cidade),
      backgroundColor: CHART_PALETTE.slice(0, porCidade.length),
      hoverBackgroundColor: CHART_PALETTE.slice(0, porCidade.length).map((c) => c + "cc"),
      borderWidth: 0,
    }],
  };

  const ufChartData = {
    labels: porUf.map((u) => u.uf_label),
    datasets: [{
      data: porUf.map((u) => u.qtd_per_uf),
      backgroundColor: CHART_PALETTE.slice(0, porUf.length),
      hoverBackgroundColor: CHART_PALETTE.slice(0, porUf.length).map((c) => c + "cc"),
      borderWidth: 0,
    }],
  };

  // --- Desempenho chart ---
  const desempenhoChartData = {
    labels: desempenho.map((d) => d.anunciante_label),
    datasets: [
      {
        label: "Vendidos",
        data: desempenho.map((d) => d.qtd_vendido),
        backgroundColor: "#10b981cc",
        hoverBackgroundColor: "#10b981",
        borderRadius: 4,
        maxBarThickness: 32,
      },
      {
        label: "Não Vendidos",
        data: desempenho.map((d) => d.qtd_nao_vendido),
        backgroundColor: "#ef4444cc",
        hoverBackgroundColor: "#ef4444",
        borderRadius: 4,
        maxBarThickness: 32,
      },
    ],
  };

  const desempenhoChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: 600 as const } },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleFont: { size: 13, weight: 700 as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          afterBody: (ctx: any) => {
            const idx = ctx[0].dataIndex;
            const item = desempenho[idx];
            return `Publicados: ${item.qtd_publicado}  |  Conversão: ${item.percentual_vendido}%`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 10 }, color: "#94a3b8", precision: 0 },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 11, weight: 600 as const }, color: "#475569" },
      },
    },
  };

  const volumeChartHeight = Math.max(320, volumeVendas.length * 48);
  const receitaChartHeight = Math.max(320, receita.length * 40);
  const mediaChartHeight = Math.max(320, mediaAnuncios.length * 36);
  const desempenhoChartHeight = Math.max(320, desempenho.length * 44);


  const resetFilters = useCallback(async () => {
    setFilters({
      grupo: "todos",
      concessionaria: "todos",
      dataInicio: getFirstDayOfMonth(),
      dataFim: new Date().toISOString().split("T")[0] || "",
    });
    await fetchDash();
  }, []);


  
  return (
    <Fade in timeout={400}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* FILTROS */}
        <AnimatedBox $delay={0}>
          <RelatorioFilterBar
            values={filters}
            onChange={setFilters}
            onSearch={fetchDash}
            onClear={resetFilters}
            loading={loading}
            title="Filtros Analítico Concessionárias"
          />
        </AnimatedBox>

        {/* KPI CARDS */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }}
          gap={2.5}
        >
          {loading ? (
            <>
              <KpiCardSkeleton delay={80} />
              <KpiCardSkeleton delay={160} />
              <KpiCardSkeleton delay={240} />
              <KpiCardSkeleton delay={320} />
              <KpiCardSkeleton delay={400} />
              <KpiCardSkeleton delay={480} />
            </>
          ) : (
            <>
              <KpiCard
                label="Total Concessionárias"
                value={formatNumber(dashboard?.total_qtd_concessionaria ?? null)}
                subtitle="cadastradas"
                icon={<Store />}
                accentColor="#0284c7"
                bgColor="#e0f2fe"
                delay={80}
              />
              <KpiCard
                label="Ativas"
                value={formatNumber(dashboard?.total_qtd_concessionaria_ativa ?? null)}
                subtitle="concessionárias"
                icon={<CheckCircle />}
                accentColor="#10b981"
                bgColor="#ecfdf5"
                delay={160}
              />
              <KpiCard
                label="Inativas"
                value={formatNumber(dashboard?.total_qtd_concessionaria_inativa ?? null)}
                subtitle="concessionárias"
                icon={<Block />}
                accentColor="#ef4444"
                bgColor="#fef2f2"
                delay={240}
              />
              <KpiCard
                label="Novas no Período"
                value={formatNumber(dashboard?.qtd_nova_concessionaria ?? null)}
                subtitle="concessionárias"
                icon={<FiberNew />}
                accentColor="#8b5cf6"
                bgColor="#f5f3ff"
                delay={320}
              />
              <KpiCard
                label="Ativas c/ Publicação"
                value={formatNumber(dashboard?.total_qtd_concessionaria_ativa_com_publicacao ?? null)}
                subtitle="concessionárias"
                icon={<Publish />}
                accentColor="#0891b2"
                bgColor="#ecfeff"
                delay={400}
              />
              <KpiCard
                label="Ativas s/ Publicação"
                value={formatNumber(dashboard?.total_qtd_concessionaria_ativa_sem_publicacao ?? null)}
                subtitle="concessionárias"
                icon={<Unpublished />}
                accentColor="#f59e0b"
                bgColor="#fffbeb"
                delay={480}
              />
            </>
          )}
        </Box>

        {/* VOLUME DE VENDAS POR CONCESSIONÁRIA */}
        <AnimatedBox $delay={400}>
          {loading ? (
            <ChartSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Volume de Vendas por Concessionária
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Valor total vendido e quantidade de anúncios vendidos
              </Typography>
              <Box height={volumeChartHeight}>
                <Bar data={volumeVendasChartData} options={volumeVendasChartOptions} />
              </Box>
            </Paper>
          )}
        </AnimatedBox>

        {/* DESEMPENHO POR CONCESSIONÁRIA */}
        <AnimatedBox $delay={450}>
          {loading ? (
            <ChartSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Desempenho por Concessionária
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Quantidade de anúncios vendidos vs não vendidos por concessionária
              </Typography>
              <Box height={desempenhoChartHeight}>
                <Bar data={desempenhoChartData} options={desempenhoChartOptions} />
              </Box>
              {desempenho.length > 0 && (
                <TableContainer sx={{ mt: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell sx={thSx}>Concessionária</TableCell>
                        <TableCell sx={{ ...thSx, textAlign: "center" }}>Publicados</TableCell>
                        <TableCell sx={{ ...thSx, textAlign: "center" }}>Vendidos</TableCell>
                        <TableCell sx={{ ...thSx, textAlign: "center" }}>Não Vendidos</TableCell>
                        <TableCell sx={{ ...thSx, minWidth: 160 }}>Conversão</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {desempenho.map((row) => (
                        <TableRow key={row.num_id_concessionaria} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                          <TableCell
                            sx={{
                              py: 2,
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: "#1e293b",
                              borderBottom: "1px solid #f8fafc",
                              maxWidth: 260,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.anunciante_label}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 2,
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: "#0284c7",
                              textAlign: "center",
                              borderBottom: "1px solid #f8fafc",
                            }}
                          >
                            {row.qtd_publicado}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 2,
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: "#10b981",
                              textAlign: "center",
                              borderBottom: "1px solid #f8fafc",
                            }}
                          >
                            {row.qtd_vendido}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 2,
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: row.qtd_nao_vendido > 0 ? "#ef4444" : "#94a3b8",
                              textAlign: "center",
                              borderBottom: "1px solid #f8fafc",
                            }}
                          >
                            {row.qtd_nao_vendido}
                          </TableCell>
                          <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box flex={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={row.percentual_vendido}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: "#fef2f2",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 4,
                                      bgcolor:
                                        row.percentual_vendido >= 80
                                          ? "#10b981"
                                          : row.percentual_vendido >= 50
                                          ? "#f59e0b"
                                          : "#ef4444",
                                    },
                                  }}
                                />
                              </Box>
                              <Typography
                                fontSize="0.75rem"
                                fontWeight={700}
                                minWidth={40}
                                color={
                                  row.percentual_vendido >= 80
                                    ? "#10b981"
                                    : row.percentual_vendido >= 50
                                    ? "#f59e0b"
                                    : "#ef4444"
                                }
                              >
                                {row.percentual_vendido}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
        </AnimatedBox>

        {/* RECEITA GERADA + MEDIA ANUNCIOS MENSAIS */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg:user?.grupoAcesso.id.toString()=== UserRole.Admin ? "1fr 1fr" : "1fr" }} gap={3}>
          <AnimatedBox $delay={500}>
            {loading ? (
              <ChartSkeleton />
            ) : user?.grupoAcesso.id && user?.grupoAcesso.id.toString()=== UserRole.Admin ? (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Receita Taxa do Canal Gerada por Concessionária
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Receita total gerada no período
                </Typography>
                <Box height={receitaChartHeight}>
                  <Bar data={receitaChartData} options={receitaChartOptions} />
                </Box>
              </Paper>
            ) : (null)}
          </AnimatedBox>

          <AnimatedBox $delay={600}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Média de Anúncios Mensais
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Média de anúncios publicados por mês por concessionária
                </Typography>
                <Box height={mediaChartHeight}>
                  <Bar data={mediaAnunciosChartData} options={mediaAnunciosChartOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* TEMPO MÉDIO DE VENDA POR CONCESSIONÁRIA */}
        <AnimatedBox $delay={700}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Tempo Médio de Venda por Concessionária
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Análise detalhada dos tempos de venda (médio, mínimo e máximo)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={thSx}>Concessionária</TableCell>
                      <TableCell sx={thSx}>Vendas</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 180 }}>Proporção</TableCell>
                      <TableCell sx={thSx}>Tempo Médio</TableCell>
                      <TableCell sx={thSx}>Mínimo</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "right" }}>Máximo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tempoMedio.map((row) => (
                      <TableRow key={row.num_id_concessionaria} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#1e293b",
                            borderBottom: "1px solid #f8fafc",
                            maxWidth: 260,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.concessionaria_label}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: colorPrimary,
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          {row.total_vendas}
                        </TableCell>
                        <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box flex={1}>
                              <LinearProgress
                                variant="determinate"
                                value={(row.total_vendas / maxVendas) * 100}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: "#f1f5f9",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    bgcolor: colorPrimary,
                                  },
                                }}
                              />
                            </Box>
                            <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={32}>
                              {Math.round((row.total_vendas / maxVendas) * 100)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#0284c7",
                            borderBottom: "1px solid #f8fafc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatMinutesToReadable(row.tempo_medio_minuto)}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#10b981",
                            borderBottom: "1px solid #f8fafc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatMinutesToReadable(row.tempo_min_minuto)}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#ef4444",
                            textAlign: "right",
                            borderBottom: "1px solid #f8fafc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatMinutesToReadable(row.tempo_max_minuto)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>

        {/* POR CIDADE + POR UF */}
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={3}>
          <AnimatedBox $delay={800}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Concessionárias por Cidade
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição geográfica por cidade
                </Typography>
                <Box height={320} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut data={cidadeChartData} options={doughnutOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>

          <AnimatedBox $delay={900}>
            {loading ? (
              <ChartSkeleton />
            ) : (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9", height: "100%" }}>
                <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                  Concessionárias por UF
                </Typography>
                <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                  Distribuição geográfica por estado
                </Typography>
                <Box height={320} display="flex" alignItems="center" justifyContent="center">
                  <Doughnut data={ufChartData} options={doughnutOptions} />
                </Box>
              </Paper>
            )}
          </AnimatedBox>
        </Box>

        {/* POR GRUPO */}
        <AnimatedBox $delay={1000}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f1f5f9" }}>
              <Typography fontWeight={800} fontSize="1rem" color="#1e293b" mb={0.5}>
                Concessionárias por Grupo
              </Typography>
              <Typography fontSize="0.8rem" color="#94a3b8" fontWeight={500} mb={3}>
                Quantidade de concessionárias ativas por grupo econômico
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={thSx}>Grupo</TableCell>
                      <TableCell sx={{ ...thSx, textAlign: "center" }}>Qtd Concessionárias</TableCell>
                      <TableCell sx={{ ...thSx, minWidth: 200 }}>Proporção</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const totalGrupo = porGrupo.reduce((sum, g) => sum + g.qtd_per_grupo, 0) || 1;
                      return porGrupo.map((row) => (
                        <TableRow key={row.grupo_label} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                          <TableCell
                            sx={{
                              py: 2,
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: "#1e293b",
                              borderBottom: "1px solid #f8fafc",
                            }}
                          >
                            {row.grupo_label}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 2,
                              fontSize: "0.85rem",
                              fontWeight: 800,
                              color: colorPrimary,
                              textAlign: "center",
                              borderBottom: "1px solid #f8fafc",
                            }}
                          >
                            {row.qtd_per_grupo}
                          </TableCell>
                          <TableCell sx={{ py: 2, borderBottom: "1px solid #f8fafc" }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box flex={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(row.qtd_per_grupo / totalGrupo) * 100}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: "#f1f5f9",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 4,
                                      bgcolor: "#8b5cf6",
                                    },
                                  }}
                                />
                              </Box>
                              <Typography fontSize="0.7rem" color="#94a3b8" fontWeight={600} minWidth={32}>
                                {Math.round((row.qtd_per_grupo / totalGrupo) * 100)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </AnimatedBox>
      </Box>
    </Fade>
  );
}
